import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { MR_BEAT } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  MapPin, CheckCircle, XCircle, NavigationArrow, Path, Clock,
  Crosshair, CaretRight
} from '@phosphor-icons/react';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const doctorIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const visitedIcon = new L.DivIcon({
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#34C759;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.2)"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="white" viewBox="0 0 256 256"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  className: '',
});

const pendingIcon = new L.DivIcon({
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#FF3B30;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.2)"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="white" viewBox="0 0 256 256"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Z"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  className: '',
});

export default function MRBeatPage() {
  const [plans, setPlans] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitDialog, setVisitDialog] = useState(null);
  const [showMap, setShowMap] = useState(true);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    Promise.all([
      api.getBeatPlans(today),
      api.getDoctors()
    ]).then(([planRes, docRes]) => {
      setPlans(planRes.data);
      setDoctors(docRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [today]);

  const allDoctorsInPlan = plans.flatMap(p => p.doctors || []);
  const mapDoctors = allDoctorsInPlan.map(pd => {
    const fullDoc = doctors.find(d => d.id === pd.id);
    return { ...pd, latitude: fullDoc?.latitude, longitude: fullDoc?.longitude, specialization: fullDoc?.specialization };
  }).filter(d => d.latitude && d.longitude);

  const center = mapDoctors.length > 0
    ? [mapDoctors.reduce((s, d) => s + d.latitude, 0) / mapDoctors.length, mapDoctors.reduce((s, d) => s + d.longitude, 0) / mapDoctors.length]
    : [28.6139, 77.2090];

  const totalVisits = allDoctorsInPlan.length;
  const completedVisits = allDoctorsInPlan.filter(d => d.visited).length;

  return (
    <div data-testid={MR_BEAT.container} className="space-y-0">
      {/* Header */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>
              Beat Plan
            </h2>
            <p className="text-[10px] font-medium" style={{ color: '#8A94A6' }}>{today}</p>
          </div>
          {/* Toggle Map/List */}
          <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: '#E2E4E9' }}>
            <button onClick={() => setShowMap(true)}
              className="px-3 py-1.5 text-[10px] font-bold transition-colors"
              style={{ background: showMap ? '#0A2540' : '#FFFFFF', color: showMap ? 'white' : '#8A94A6' }}>
              Map
            </button>
            <button onClick={() => setShowMap(false)}
              className="px-3 py-1.5 text-[10px] font-bold transition-colors"
              style={{ background: !showMap ? '#0A2540' : '#FFFFFF', color: !showMap ? 'white' : '#8A94A6' }}>
              List
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-semibold" style={{ color: '#8A94A6' }}>Progress</span>
            <span className="text-xs font-bold" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>
              {completedVisits}/{totalVisits} visits
            </span>
          </div>
          <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: '#E2E4E9' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{
              width: `${totalVisits > 0 ? (completedVisits / totalVisits) * 100 : 0}%`,
              background: completedVisits === totalVisits && totalVisits > 0 ? '#34C759' : '#007AFF'
            }} />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="px-4 py-12 text-center" style={{ color: '#8A94A6' }}>Loading beat plan...</div>
      ) : plans.length === 0 ? (
        <div className="mx-4 p-8 text-center rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <Path size={48} weight="duotone" style={{ color: '#E2E4E9' }} className="mx-auto mb-3" />
          <p className="text-sm font-semibold" style={{ color: '#0A2540' }}>No beat plan for today</p>
          <p className="text-xs mt-1" style={{ color: '#8A94A6' }}>Contact your manager for assignment</p>
        </div>
      ) : (
        <>
          {/* Map View */}
          {showMap && mapDoctors.length > 0 && (
            <div className="mx-4 rounded-xl overflow-hidden border" style={{ borderColor: '#E2E4E9', height: 260 }}>
              <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; OpenStreetMap'
                />
                {mapDoctors.map((doc, i) => (
                  <React.Fragment key={i}>
                    <Marker position={[doc.latitude, doc.longitude]} icon={doc.visited ? visitedIcon : pendingIcon}>
                      <Popup>
                        <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                          <strong>{doc.name}</strong><br />
                          <span style={{ fontSize: 11, color: '#8A94A6' }}>{doc.clinic_address}</span><br />
                          <span style={{ fontSize: 11, color: doc.visited ? '#34C759' : '#FF3B30', fontWeight: 600 }}>
                            {doc.visited ? 'Visited' : 'Pending'}
                          </span>
                        </div>
                      </Popup>
                    </Marker>
                    {!doc.visited && (
                      <Circle center={[doc.latitude, doc.longitude]} radius={2000}
                        pathOptions={{ color: '#007AFF', fillColor: '#007AFF', fillOpacity: 0.05, weight: 1, dashArray: '5 5' }} />
                    )}
                  </React.Fragment>
                ))}
              </MapContainer>
            </div>
          )}

          {/* Route Info */}
          {plans.map((plan, pi) => (
            <div key={pi} className="px-4 mt-3 space-y-2">
              <div data-testid={MR_BEAT.todayRoute} className="flex items-center gap-2 p-3 rounded-xl"
                style={{ background: '#007AFF08', border: '1px solid #007AFF20' }}>
                <NavigationArrow size={18} weight="fill" style={{ color: '#007AFF' }} />
                <div className="flex-1">
                  <p className="text-sm font-bold" style={{ color: '#0A2540' }}>Route: {plan.area}</p>
                  <p className="text-[10px]" style={{ color: '#8A94A6' }}>{plan.notes}</p>
                </div>
              </div>

              {/* Doctor Visit Cards */}
              {(plan.doctors || []).map((doc, di) => (
                <div key={di} data-testid={MR_BEAT.visitCard}
                  className="p-3.5 rounded-xl border flex items-center gap-3 active:scale-[0.98] transition-transform"
                  style={{ background: '#FFFFFF', borderColor: doc.visited ? '#34C75930' : '#E2E4E9' }}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: doc.visited ? '#34C75910' : '#F5F6F8' }}>
                    {doc.visited ?
                      <CheckCircle size={22} weight="fill" style={{ color: '#34C759' }} /> :
                      <MapPin size={22} weight="duotone" style={{ color: '#FF3B30' }} />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate" style={{ color: '#0A2540' }}>{doc.name}</p>
                    <p className="text-[10px] truncate" style={{ color: '#8A94A6' }}>{doc.clinic_address}</p>
                    {doc.visited && (
                      <span className="text-[9px] font-bold uppercase mt-0.5 inline-block" style={{ color: '#34C759' }}>Verified</span>
                    )}
                  </div>
                  {!doc.visited ? (
                    <Button
                      data-testid={MR_BEAT.logVisitBtn}
                      onClick={() => setVisitDialog({ doctor_id: doc.id, beat_plan_id: plan._id })}
                      className="h-9 px-4 text-xs font-bold rounded-lg shrink-0"
                      style={{ background: '#007AFF', color: 'white' }}
                    >
                      <Crosshair size={14} className="mr-1" /> Check In
                    </Button>
                  ) : (
                    <CaretRight size={16} style={{ color: '#E2E4E9' }} />
                  )}
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      {/* Visit Dialog */}
      <VisitDialog data={visitDialog} onClose={() => setVisitDialog(null)} onSaved={() => {
        setVisitDialog(null);
        api.getBeatPlans(today).then(r => setPlans(r.data));
      }} />
    </div>
  );
}

function VisitDialog({ data, onClose, onSaved }) {
  const [notes, setNotes] = useState('');
  const [logging, setLogging] = useState(false);
  const [result, setResult] = useState(null);

  if (!data) return null;

  const logVisit = async () => {
    setLogging(true);
    let lat = 28.6139, lng = 77.2090;
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000, enableHighAccuracy: true })
      );
      lat = pos.coords.latitude;
      lng = pos.coords.longitude;
    } catch {}
    try {
      const { data: res } = await api.logVisit({
        doctor_id: data.doctor_id,
        beat_plan_id: data.beat_plan_id,
        latitude: lat,
        longitude: lng,
        notes
      });
      setResult(res);
    } catch (err) {
      console.error(err);
    }
    setLogging(false);
  };

  return (
    <Dialog open={!!data} onOpenChange={onClose}>
      <DialogContent className="max-w-sm p-5">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>GPS Visit Verification</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-4">
            <div className="p-6 rounded-xl text-center" style={{ background: result.verified ? '#F0FFF4' : '#FFF0F0' }}>
              {result.verified ?
                <CheckCircle size={56} weight="fill" style={{ color: '#34C759' }} /> :
                <XCircle size={56} weight="fill" style={{ color: '#FF3B30' }} />
              }
              <p className="text-lg font-extrabold mt-3" style={{
                color: result.verified ? '#34C759' : '#FF3B30',
                fontFamily: 'Manrope, sans-serif'
              }}>
                {result.verified ? 'Visit Verified!' : 'Outside Range'}
              </p>
              <p className="text-sm mt-1" style={{ color: '#8A94A6' }}>
                Distance: <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{result.distance_km} km</strong>
                {!result.verified && ' (must be within 2km)'}
              </p>
            </div>
            <Button onClick={onSaved} className="w-full h-12 text-sm font-bold rounded-xl" style={{ background: '#0A2540', color: 'white' }}>
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-xl" style={{ background: '#F5F6F8' }}>
              <div className="flex items-center gap-2 mb-2">
                <Crosshair size={16} weight="duotone" style={{ color: '#007AFF' }} />
                <p className="text-xs font-bold" style={{ color: '#0A2540' }}>2km Radius Check</p>
              </div>
              <p className="text-xs" style={{ color: '#8A94A6' }}>
                Your GPS location will be verified against the doctor's clinic address. You must be within 2km to log a verified visit.
              </p>
            </div>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Visit notes (optional)"
              className="h-20 text-sm rounded-lg"
              style={{ borderColor: '#E2E4E9' }}
            />
            <Button onClick={logVisit} disabled={logging}
              className="w-full h-12 text-sm font-bold rounded-xl"
              style={{ background: '#007AFF', color: 'white' }}>
              {logging ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Verifying GPS...
                </span>
              ) : (
                <><Crosshair size={18} className="mr-2" /> Verify & Log Visit</>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
